import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Flame, ShirtIcon, Layers, Wand2, Sparkles, Award, Users, ShieldCheck,
  Zap, Heart, ArrowRight, Target, Rocket, Cpu,
} from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, name: "Softer prints, softer hands", body: "Every default we ship is tuned so less ink lands on the shirt. Softer hand feel, better breathability, fewer complaints." },
  { icon: Zap, name: "Prepress in seconds", body: "What used to take 30 minutes in Photoshop happens in one browser tab. Fabric picker, LPI, angle, underbase — done." },
  { icon: Cpu, name: "AI that respects your art", body: "We use AI where it adds real value: cleanup, background removal, retouch. Never to replace your creative direction." },
  { icon: Heart, name: "Built by print shops, for print shops", body: "We're not a Silicon Valley SaaS bolted onto a printer. Every feature exists because a shop asked for it." },
];

const TIMELINE = [
  { year: "2023", title: "The itch", body: "Founders were running a small DTG operation, hand-cranking halftones in Photoshop. Every job ate 30-45 min just on prepress." },
  { year: "2024", title: "First prototype", body: "A browser tool that could bang out a press-ready halftone + underbase in under a minute. Beta shipped to 30 shops." },
  { year: "2025", title: "12 styles + AI Vector", body: "Expanded from AM round dots to 12 halftone patterns. Added Gemini Nano Banana for AI vector cleanup." },
  { year: "2026", title: "Watermark + Photo Upgrader", body: "Added Evoto-style AI retouch and a one-click watermark remover. Also launched All-Access Lifetime." },
];

const STATS = [
  { n: "12", label: "Halftone styles built-in" },
  { n: "< 60s", label: "Average prepress time" },
  { n: "300 DPI", label: "Every export, always" },
  { n: "15-day", label: "Money-back guarantee" },
];

export default function AboutPage() {
  return (
    <div className="grain">
      <Navbar />
      <section className="relative overflow-hidden">
        <div className="forge-glow absolute inset-0" />
        <div className="dot-grid-bg absolute inset-0 opacity-25" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-28">
          <span className="chip"><Flame className="h-3 w-3 text-[#4aa4ff]" /> About Furnace Design Lab</span>
          <h1 className="display-font mt-4 max-w-4xl text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            <span className="hero-headline">Forged by print shops.</span>{" "}
            <span className="ember-text">Built for the press.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            We're a small crew of screen printers, DTF operators and software people who got tired of fighting Photoshop every time a customer sent artwork. So we built Furnace Design Lab: prepress you can actually enjoy — press-ready halftones, AI vector cleanup, watermark removal and pro-grade photo retouching, all in one browser tab.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup"><Button className="btn-ember h-12 px-6 font-semibold">Try it free <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/pricing"><Button variant="outline" className="h-12">See plans</Button></Link>
          </div>
        </div>
      </section>

      <section className="border-y border-border/60 bg-background/60">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-10 sm:px-6 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-card p-6">
              <div className="display-font text-4xl font-bold ember-text">{s.n}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="relative py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <span className="chip"><Target className="h-3 w-3 text-[#4aa4ff]" /> Our mission</span>
            <h2 className="display-font mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
              Kill the <span className="ember-text">plastic-feeling DTF print</span> — forever.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Print shops shouldn't need a graphics degree to make a shirt feel soft. Our mission is to make press-ready halftones a one-click job — no matter your background, your budget or the artwork your customer sends you at 2 AM.
            </p>
            <p className="mt-3 text-muted-foreground">
              Softer hand feel. Less ink. Fewer complaints. More reorders. That's the whole product.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-blue-500/10 to-amber-400/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border chrome-border">
              <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956" alt="Screen printer" className="h-[420px] w-full object-cover opacity-90" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-border/60 bg-gradient-to-b from-transparent via-secondary/20 to-transparent py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="chip mx-auto"><Award className="h-3 w-3 text-[#f5c845]" /> What we believe</span>
            <h2 className="display-font mt-3 text-4xl font-bold tracking-tight sm:text-5xl">Four principles we ship by.</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {VALUES.map((v) => (
              <div key={v.name} className="card-hover rounded-2xl border border-border bg-card p-7">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]"><v.icon className="h-5 w-5" /></div>
                <h3 className="display-font text-xl font-bold">{v.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="text-center">
            <span className="chip mx-auto"><Rocket className="h-3 w-3 text-[#4aa4ff]" /> How we got here</span>
            <h2 className="display-font mt-3 text-4xl font-bold tracking-tight sm:text-5xl">From a workshop bench to your browser.</h2>
          </div>
          <ol className="mt-12 space-y-6 border-l-2 border-border pl-6">
            {TIMELINE.map((t) => (
              <li key={t.year} className="relative">
                <span className="absolute -left-[35px] top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#4aa4ff] text-[10px] font-bold text-[#05070d]">•</span>
                <div className="mono text-[11px] uppercase tracking-widest text-[#f5c845]">{t.year}</div>
                <h3 className="display-font mt-1 text-xl font-bold">{t.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Team callout */}
      <section className="relative border-y border-border/60 bg-background/60 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="chip mx-auto"><Users className="h-3 w-3 text-[#4aa4ff]" /> Who's behind it</span>
            <h2 className="display-font mt-3 text-4xl font-bold tracking-tight sm:text-5xl">A small team, tight roadmap.</h2>
            <p className="mt-3 text-muted-foreground">We're intentionally small so we can move fast and stay close to the shops using us. If you email us, a human replies — usually the same one who wrote the code.</p>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { role: "Prepress lead", body: "20 years running a screen shop. Owns halftone defaults, LPI presets and fabric tuning." },
              { role: "AI + software", body: "Ships the browser tool, the AI pipelines and the export codepath. Refuses to let anything run slower than one second." },
              { role: "Support + community", body: "Answers your email, runs the Facebook group, gives artwork advice on request." },
            ].map((p, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-6">
                <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">Team</div>
                <h3 className="display-font mt-1 text-lg font-bold">{p.role}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="display-font text-4xl font-bold tracking-tight sm:text-5xl">Ready to try softer prints?</h2>
          <p className="mt-3 text-muted-foreground">Sign up in 60 seconds. Your first three exports this month are on us.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/signup"><Button className="btn-ember h-12 px-6 font-semibold">Create a free account</Button></Link>
            <Link to="/pricing"><Button variant="outline" className="h-12">See pricing</Button></Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
